import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useFloating, autoUpdate, offset, flip, shift, size, arrow } from '@floating-ui/react-dom';
import { Popover, Transition } from '@headlessui/react';
import { Fragment, useRef, useState } from 'react';
import { classNames } from "../../utils/helpers";
import s from './Tooltip.module.css';
import { Text } from "../text";
import { Icon } from "../icon";
import { Info, Close } from '@az/starc-ui-icons';
export const Tooltip = ({ headerText, bodyText, width = 350, placement = 'top', buttonTitle = 'Details', svg = Info, svgSize = 7, children, customTrigger, arrowOffset = 0, variant = 'default', inline = false, triggerType = 'click', onClick, }) => {
    var _a;
    const arrowRef = useRef(null);
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const { refs: { setReference: reference, setFloating: floating }, floatingStyles, middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }, placement: floatingPlacement, } = useFloating({
        whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, {
            animationFrame: true,
            elementResize: false,
        }),
        placement,
        middleware: [
            offset(10),
            flip(),
            shift(),
            size({
                apply({ elements, availableWidth }) {
                    let setWidth;
                    if (availableWidth < width) {
                        setWidth = availableWidth;
                    }
                    else {
                        setWidth = width;
                    }
                    Object.assign(elements.floating.style, {
                        maxWidth: `${setWidth}px`,
                    });
                },
            }),
            arrow({ element: arrowRef }),
        ],
    });
    const arrowSide = (_a = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[floatingPlacement.split('-')[0]]) !== null && _a !== void 0 ? _a : floatingPlacement;
    const closeTriggerClassName = classNames(s['close-trigger'], variant === 'alt' && s['close-trigger-alt']);
    const rootClassName = inline ? s['info-icon-inline'] : undefined;
    const triggerTypeHover = triggerType === 'hover';
    return (_jsxs(Popover, { "data-testid": "st-tooltip", className: rootClassName, children: [_jsx(Popover.Button, { "data-testid": "st-tooltip-button", className: !customTrigger ? s['info-trigger'] : s['custom-trigger'], ref: reference, title: buttonTitle, "aria-label": buttonTitle, onMouseEnter: () => triggerTypeHover && setIsPanelVisible(true), onMouseLeave: () => triggerTypeHover && setIsPanelVisible(false), onClick: onClick, children: customTrigger ? (customTrigger) : (_jsx(Icon, { svg: svg, size: svgSize, className: s['info-icon'], color: "on-secondary" })) }), _jsx(Transition, { show: triggerTypeHover ? isPanelVisible : undefined, as: Fragment, children: _jsxs(Popover.Panel, { "data-testid": "st-tooltip-panel", className: s.content, ref: floating, style: floatingStyles, children: [_jsx("div", { className: s.arrow, ref: arrowRef, style: {
                                left: arrowX != null
                                    ? `${arrowX + (arrowSide === 'top' || arrowSide === 'bottom' ? arrowOffset : 0)}px`
                                    : '',
                                top: arrowY != null
                                    ? `${arrowY + (arrowSide === 'left' || arrowSide === 'right' ? arrowOffset : 0)}px`
                                    : '',
                                [arrowSide]: '-8px',
                            } }), _jsxs("div", { role: "alert", children: [headerText && variant === 'default' && (_jsx(Text, { variant: "display-4", className: s['header-text'], textCase: "uppercase", lineHeight: "140", color: "primary", children: headerText })), headerText && variant === 'alt' && (_jsx(Text, { className: s['header-text'], fontFamily: "body", size: "125", weight: "bold", lineHeight: "140", children: headerText })), bodyText && variant === 'default' && (_jsx(Text, { variant: "small-body", className: s['body-text'], color: "primary", lineHeight: "140", children: bodyText })), bodyText && variant === 'alt' && (_jsx(Text, { variant: "small-body", className: s['body-text'], attributes: { style: { lineHeight: 1.57 } }, color: "gray-500", children: bodyText })), children] }), !triggerTypeHover && (_jsx(Popover.Button, { "aria-label": "Close help tooltip", className: closeTriggerClassName, children: _jsx(Icon, { svg: Close, size: 8, className: s['close-icon'] }) }))] }) })] }));
};
